import React from 'react';
import LegalPageLayout, { AccordionSection } from './LegalPageLayout';

const GDPR = () => (
  <LegalPageLayout
    title="GDPR Compliance"
    subtitle="OpticVance is committed to protecting the rights of individuals in the European Union. This page outlines our compliance with the General Data Protection Regulation."
    lastUpdated="March 3, 2026"
  >
    <AccordionSection title="Our Commitment to GDPR" defaultOpen>
      <p>The General Data Protection Regulation (GDPR) is one of the most comprehensive data privacy regulations in the world. OpticVance is fully committed to GDPR compliance and has implemented comprehensive measures to ensure the protection of personal data belonging to EU residents.</p>
      <p>We serve as both a Data Controller (for our own business purposes) and a Data Processor (when processing data on behalf of our customers). We maintain clear documentation of our processing activities and have appointed a dedicated Data Protection Officer (DPO).</p>
    </AccordionSection>

    <AccordionSection title="Lawful Basis for Processing">
      <p>We process personal data only when we have a lawful basis to do so, including:</p>
      <ul className="list-disc list-inside space-y-1.5 ml-2">
        <li><strong className="text-white">Consent:</strong> Where you have given clear consent for us to process your personal data for a specific purpose</li>
        <li><strong className="text-white">Contract:</strong> Where processing is necessary for the performance of a contract with you</li>
        <li><strong className="text-white">Legitimate Interest:</strong> Where processing is necessary for our legitimate interests, provided these are not overridden by your rights</li>
        <li><strong className="text-white">Legal Obligation:</strong> Where processing is necessary for compliance with a legal obligation</li>
      </ul>
    </AccordionSection>

    <AccordionSection title="Your Rights Under GDPR">
      <p>Under GDPR, you have the following rights regarding your personal data:</p>
      <ul className="list-disc list-inside space-y-1.5 ml-2">
        <li><strong className="text-white">Right of Access (Art. 15):</strong> Obtain confirmation of whether we process your personal data and request a copy</li>
        <li><strong className="text-white">Right to Rectification (Art. 16):</strong> Request correction of inaccurate personal data</li>
        <li><strong className="text-white">Right to Erasure (Art. 17):</strong> Request deletion of your personal data ("right to be forgotten")</li>
        <li><strong className="text-white">Right to Restrict Processing (Art. 18):</strong> Request limitation of processing in certain circumstances</li>
        <li><strong className="text-white">Right to Data Portability (Art. 20):</strong> Receive your data in a structured, commonly used, machine-readable format</li>
        <li><strong className="text-white">Right to Object (Art. 21):</strong> Object to processing based on legitimate interests or direct marketing</li>
        <li><strong className="text-white">Right Not to Be Subject to Automated Decisions (Art. 22):</strong> Not be subject to decisions based solely on automated processing</li>
      </ul>
      <p>To exercise any of these rights, please contact our Data Protection Officer at <span className="text-primary">help@opticvance.com</span>. We will respond within 30 days.</p>
    </AccordionSection>

    <AccordionSection title="Data Processing Agreements">
      <p>OpticVance enters into Data Processing Agreements (DPAs) with all customers who process EU personal data through our platform. Our DPA includes:</p>
      <ul className="list-disc list-inside space-y-1.5 ml-2">
        <li>Clear description of processing activities, purposes, and data categories</li>
        <li>Obligations regarding sub-processors, including prior notification of changes</li>
        <li>Technical and organizational security measures (Article 32)</li>
        <li>Assistance with data subject requests and breach notification</li>
        <li>Data deletion or return upon termination of the agreement</li>
        <li>Audit rights for the data controller</li>
      </ul>
    </AccordionSection>

    <AccordionSection title="International Data Transfers">
      <p>When personal data is transferred outside the European Economic Area (EEA), we ensure adequate protection through:</p>
      <ul className="list-disc list-inside space-y-1.5 ml-2">
        <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
        <li>Adequacy decisions where applicable</li>
        <li>EU-US Data Privacy Framework certification</li>
        <li>Transfer Impact Assessments for each data flow</li>
        <li>Additional supplementary measures as recommended by the EDPB</li>
      </ul>
    </AccordionSection>

    <AccordionSection title="Data Protection Impact Assessments">
      <p>We conduct Data Protection Impact Assessments (DPIAs) for all processing activities that are likely to result in high risk to the rights and freedoms of individuals. This includes new product features, changes to data processing operations, and new third-party integrations.</p>
      <p>Our DPIA process involves identifying risks, evaluating their likelihood and severity, and implementing appropriate mitigating measures before processing begins.</p>
    </AccordionSection>

    <AccordionSection title="Contact Our Data Protection Officer">
      <p>If you have any questions about GDPR or wish to exercise your rights, please contact our Data Protection Officer:</p>
      <div className="mt-2 p-4 rounded-lg bg-primary/5 border border-primary/10">
        <p className="text-white font-medium">Data Protection Officer</p>
        <p>Email: <span className="text-primary">help@opticvance.com</span></p>
        <p>Address: OpticVance Inc., 18 Waverly Pl, New York, NY, USA</p>
        <p>Postal Code: 10003</p>
        <p>Phone: +16468143909</p>
        <p className="mt-2 text-xs">You also have the right to lodge a complaint with your local supervisory authority.</p>
      </div>
    </AccordionSection>
  </LegalPageLayout>
);

export default GDPR;
